<template>
  <div class="space-y-6">
    <!-- Alerts -->
    <UiKitAlert v-if="errorMessage" color="danger">{{ errorMessage }}</UiKitAlert>
    <UiKitAlert v-if="successMessage" color="success">{{ successMessage }}</UiKitAlert>

    <div class="space-y-4">
      <!-- Add New Slide -->
      <div class="rounded-xl border border-hui-divider bg-hui-content1 overflow-hidden">
        <div class="flex items-center gap-2 px-4 py-3 border-b border-hui-divider">
          <Icon name="heroicons:plus-circle" class="w-5 h-5 text-hui-foreground/50" />
          <span class="font-semibold text-hui-foreground">Добавить новый слайд</span>
        </div>

        <div class="p-4 space-y-4">
          <!-- Основное изображение -->
          <div>
            <label class="block text-sm font-medium text-hui-foreground/80 mb-2">
              Главное изображение (обложка)
              <span class="text-hui-danger">*</span>
            </label>
            <div class="flex items-center gap-4">
              <input ref="mainImageInput" type="file" accept="image/*" class="hidden" @change="handleMainImageSelect" />
              <UiKitButton type="button" color="primary" variant="bordered" @click="mainImageInput?.click()">
                <template #leading><Icon name="heroicons:photo" class="w-4 h-4" /></template>
                Выбрать изображение
              </UiKitButton>
              <span v-if="newSlide.mainImageFile" class="text-sm text-hui-success">
                ✓ {{ newSlide.mainImageFile.name }}
              </span>
            </div>
            <p class="text-xs text-hui-foreground/60 mt-1">Это изображение будет отображаться как обложка слайда</p>
          </div>

          <!-- Внутренние изображения -->
          <div>
            <label class="block text-sm font-medium text-hui-foreground/80 mb-2">
              Внутренние изображения (необязательно)
            </label>
            <div class="flex items-center gap-4">
              <input
                ref="internalImagesInput"
                type="file"
                accept="image/*"
                multiple
                class="hidden"
                @change="handleInternalImagesSelect"
              />
              <UiKitButton type="button" color="default" variant="bordered" @click="internalImagesInput?.click()">
                <template #leading><Icon name="heroicons:photo" class="w-4 h-4" /></template>
                Выбрать изображения
              </UiKitButton>
              <span v-if="newSlide.internalImageFiles.length > 0" class="text-sm text-hui-success">
                ✓ Выбрано: {{ newSlide.internalImageFiles.length }} файлов
              </span>
            </div>
            <p class="text-xs text-hui-foreground/60 mt-1">Эти изображения будут доступны внутри слайда</p>
          </div>

          <!-- Остальные поля -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <UiKitInput v-model="newSlide.title" label="Заголовок" placeholder="Заголовок слайда" />

            <UiKitInput v-model="newSlide.description" label="Описание" placeholder="Описание слайда" />

            <USelectMenu
              v-model="newSlide.action_type"
              :options="actionTypes"
              option-attribute="label"
              value-attribute="value"
              placeholder="Выберите действие"
              @update:model-value="handleActionTypeChange"
            />

            <UiKitInput v-model="newSlide.action_label" label="Метка кнопки" placeholder="Например: Заказать сейчас" />

            <UiKitInput v-model.number="newSlide.sort_order" type="number" label="Порядок сортировки" placeholder="1" />

            <div class="flex items-center justify-between gap-4">
              <label class="text-sm font-medium text-hui-foreground/80">Активен</label>
              <UiKitSwitch v-model="newSlide.is_active" />
            </div>
          </div>

          <!-- Action Data (зависит от типа) -->
          <div v-if="newSlide.action_type !== 'none'" class="p-3 bg-hui-default-100 rounded-lg">
            <label class="block text-sm font-medium text-hui-foreground/80 mb-2">Данные действия (JSON)</label>
            <textarea
              v-model="newSlide.action_data_json"
              rows="3"
              placeholder='{"productId": "42"}'
              class="w-full p-2 border border-hui-divider rounded-lg text-sm font-mono bg-hui-content1 text-hui-foreground focus:outline-none focus:border-hui-primary"
            />
            <p class="text-xs text-hui-foreground/60 mt-1">Формат зависит от типа действия</p>
          </div>

          <UiKitButton
            type="button"
            color="primary"
            :loading="uploading"
            :disabled="!newSlide.mainImageFile"
            @click="createNewSlide"
          >
            <template #leading><Icon name="heroicons:plus" class="w-4 h-4" /></template>
            Создать слайд
          </UiKitButton>
        </div>
      </div>

      <!-- Existing Slides -->
      <div class="rounded-xl border border-hui-divider bg-hui-content1 overflow-hidden">
        <div class="flex items-center gap-2 px-4 py-3 border-b border-hui-divider">
          <Icon name="heroicons:queue-list" class="w-5 h-5 text-hui-foreground/50" />
          <span class="font-semibold text-hui-foreground">Существующие слайды</span>
        </div>

        <div class="p-4">
          <div v-if="slides.length === 0" class="text-center py-8 text-hui-foreground/60">
            Нет слайдов. Создайте первый слайд выше.
          </div>

          <div class="space-y-4">
            <div
              v-for="(slide, index) in slides"
              :key="slide.id || index"
              class="rounded-lg border border-hui-divider p-4 bg-hui-default-100"
            >
              <!-- Заголовок и действия -->
              <div class="flex justify-between items-start mb-4">
                <div>
                  <h4 class="font-medium text-lg text-hui-foreground">{{ slide.title || `Слайд ${index + 1}` }}</h4>
                  <div class="flex gap-3 mt-1 text-xs text-hui-foreground/60">
                    <span>{{ slide.views_count || 0 }} просмотров</span>
                    <span>{{ slide.clicks_count || 0 }} кликов</span>
                    <span v-if="slide.ctr">CTR: {{ slide.ctr?.toFixed(1) }}%</span>
                  </div>
                </div>
                <div class="flex gap-2 items-center">
                  <UiKitSwitch v-model="slide.is_active" @update:model-value="updateSlideStatus(slide)" />
                  <UiKitButton
                    type="button"
                    color="danger"
                    variant="light"
                    size="sm"
                    @click="confirmDeleteSlide(slide, index)"
                  >
                    <Icon name="heroicons:trash" class="w-4 h-4" />
                  </UiKitButton>
                </div>
              </div>

              <!-- Текущая обложка -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-hui-foreground/80 mb-2">Текущая обложка</label>
                <div v-if="slide.image_url" class="relative">
                  <img
                    :src="slide.image_url"
                    :alt="slide.title || 'Slide cover'"
                    :data-slide-id="slide.id"
                    class="w-full max-w-md h-48 object-cover rounded-lg border border-hui-divider bg-hui-default-100"
                    loading="lazy"
                    @error="handleImageError($event)"
                    @load="handleImageLoad($event)"
                  />
                  <div
                    v-if="imageLoadErrors[slide.id!]"
                    class="mt-2 p-3 bg-hui-danger/10 rounded-lg text-sm text-hui-danger"
                  >
                    Ошибка загрузки изображения
                  </div>
                  <div class="mt-1 text-xs text-hui-foreground/60 break-all">URL: {{ slide.image_url }}</div>
                </div>
                <div v-else class="p-8 bg-hui-default-100 rounded-lg text-center text-hui-foreground/60">
                  Изображение не загружено
                </div>
              </div>

              <!-- Внутренние изображения -->
              <div v-if="slide.internal_slides && slide.internal_slides.length > 0" class="mb-4">
                <label class="block text-sm font-medium text-hui-foreground/80 mb-2">Внутренние изображения</label>
                <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  <div v-for="(internalImg, imgIdx) in slide.internal_slides" :key="imgIdx" class="relative group">
                    <img
                      :src="internalImg.image"
                      :alt="`Internal ${imgIdx + 1}`"
                      class="w-full h-20 object-cover rounded border-2 border-hui-divider hover:border-hui-primary transition-colors"
                    />
                    <div
                      class="absolute inset-0 bg-hui-primary/0 group-hover:bg-hui-primary/20 rounded flex items-center justify-center transition-opacity"
                    >
                      <UIcon
                        name="i-heroicons-check-circle"
                        class="w-6 h-6 text-white opacity-0 group-hover:opacity-100"
                      />
                    </div>
                  </div>
                </div>
                <p class="text-xs text-hui-foreground/60 mt-2">
                  Внутренние изображения отображаются в stories. Для изменения обложки загрузите новое главное
                  изображение.
                </p>
              </div>

              <!-- Добавить внутренние изображения -->
              <div class="mb-4">
                <input
                  :ref="(el) => setInternalImageRef(slide.id!, el as HTMLInputElement)"
                  type="file"
                  accept="image/*"
                  multiple
                  class="hidden"
                  @change="handleAddInternalImages(slide, $event)"
                />
                <UiKitButton
                  type="button"
                  color="default"
                  variant="bordered"
                  size="sm"
                  @click="getInternalImageRef(slide.id!)?.click()"
                >
                  <template #leading><Icon name="heroicons:plus" class="w-4 h-4" /></template>
                  Добавить внутренние изображения
                </UiKitButton>
              </div>

              <!-- Обновить главное изображение -->
              <div class="mb-4">
                <input
                  :ref="(el) => setMainImageRef(slide.id!, el as HTMLInputElement)"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handleUpdateMainImage(slide, $event)"
                />
                <UiKitButton
                  type="button"
                  color="primary"
                  variant="bordered"
                  size="sm"
                  @click="getMainImageRef(slide.id!)?.click()"
                >
                  <template #leading><Icon name="heroicons:arrow-up-tray" class="w-4 h-4" /></template>
                  Заменить обложку (загрузить новую)
                </UiKitButton>
              </div>

              <!-- Редактирование полей -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <UiKitInput v-model="slide.title" label="Заголовок" placeholder="Заголовок слайда" />

                <UiKitInput v-model="slide.description" label="Описание" placeholder="Описание слайда" />

                <UiKitInput v-model.number="slide.sort_order" type="number" label="Порядок" placeholder="1" />
              </div>

              <UiKitButton type="button" color="primary" size="sm" class="mt-4" @click="updateSlideData(slide)">
                <template #leading><Icon name="heroicons:check" class="w-4 h-4" /></template>
                Сохранить изменения
              </UiKitButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="flex justify-end pt-4 border-t border-hui-divider">
      <UiKitButton type="button" color="default" variant="flat" @click="modalStore.close()">Закрыть</UiKitButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import consola from 'consola';
import { ref, onMounted } from 'vue';
import { useApi } from '@shared/api';
import { useModalStore } from '@shared/model';
import { useKitToast } from '@shared/ui/kit';

const emit = defineEmits<{
  'update:size': [size: string];
}>();

const modalStore = useModalStore();

interface NewsSlide {
  id?: number | string;
  title: string;
  description: string;
  image_url: string;
  internal_slides?: Array<{ image: string; order: number }>;
  action_type?: string;
  action_data?: any;
  action_label?: string;
  sort_order: number;
  is_active: boolean;
  views_count?: number;
  clicks_count?: number;
  ctr?: number;
}

interface NewSlideForm {
  title: string;
  description: string;
  mainImageFile: File | null;
  internalImageFiles: File[];
  action_type: string | { label: string; value: string };
  action_label: string;
  action_data_json: string;
  sort_order: number;
  is_active: boolean;
}

const { toast: kitToast } = useKitToast();
const toast = {
  add: (o: { title?: string; description?: string; color?: string; timeout?: number }) =>
    kitToast({
      color:
        o.color === 'green' ? 'success' : o.color === 'red' ? 'danger' : o.color === 'orange' ? 'warning' : 'default',
      title: o.title,
      description: o.description,
      timeout: o.timeout,
    }),
};

const loading = ref(false);
const uploading = ref(false);
const successMessage = ref('');
const errorMessage = ref('');
const imageLoadErrors = ref<Record<string | number, string>>({});

const slides = ref<NewsSlide[]>([]);

// Refs для file inputs
const mainImageInput = ref<HTMLInputElement>();
const internalImagesInput = ref<HTMLInputElement>();
const internalImageRefs = ref<Record<string | number, HTMLInputElement>>({});
const mainImageRefs = ref<Record<string | number, HTMLInputElement>>({});

const actionTypes = [
  { label: 'Без действия', value: 'none' },
  { label: 'Внешняя ссылка', value: 'external_link' },
  { label: 'Открыть товар', value: 'open_product' },
  { label: 'Скопировать промокод', value: 'copy_promo' },
  { label: 'Навигация', value: 'navigate' },
  { label: 'Поделиться реферальной ссылкой', value: 'share_referral' },
];

const newSlide = ref<NewSlideForm>({
  title: '',
  description: '',
  mainImageFile: null,
  internalImageFiles: [],
  action_type: 'none',
  action_label: '',
  action_data_json: '',
  sort_order: 1,
  is_active: true,
});

// Функции для работы с refs
function setInternalImageRef(id: string | number, el: HTMLInputElement) {
  if (el) internalImageRefs.value[id] = el;
}

function getInternalImageRef(id: string | number) {
  return internalImageRefs.value[id];
}

function setMainImageRef(id: string | number, el: HTMLInputElement) {
  if (el) mainImageRefs.value[id] = el;
}

function getMainImageRef(id: string | number) {
  return mainImageRefs.value[id];
}

// Обработчики выбора файлов
function handleMainImageSelect(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files[0]) {
    newSlide.value.mainImageFile = target.files[0];
  }
}

function handleInternalImagesSelect(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    newSlide.value.internalImageFiles = [...target.files];
  }
}

// Обработчики загрузки изображений
function handleImageError(event: Event) {
  const img = event.target as HTMLImageElement;
  const slideId = img.dataset.slideId;
  if (slideId) {
    imageLoadErrors.value[slideId] = `Не удалось загрузить изображение`;
    consola.error('Ошибка загрузки изображения для слайда:', slideId, img.src);
  }
}

function handleImageLoad(event: Event) {
  const img = event.target as HTMLImageElement;
  const slideId = img.dataset.slideId;
  if (slideId && imageLoadErrors.value[slideId]) {
    delete imageLoadErrors.value[slideId];
  }
}

// Нормализация action_type - всегда сохраняем как строку
function handleActionTypeChange(value: any) {
  // Если пришёл объект, извлекаем value, иначе используем как есть
  if (typeof value === 'object' && value !== null) {
    newSlide.value.action_type = value.value || 'none';
  } else {
    newSlide.value.action_type = value || 'none';
  }
}

// Создание нового слайда
async function createNewSlide() {
  if (!newSlide.value.mainImageFile) {
    errorMessage.value = 'Выберите главное изображение';
    return;
  }

  uploading.value = true;
  errorMessage.value = '';

  try {
    const formData = new FormData();

    // Главное изображение
    formData.append('image', newSlide.value.mainImageFile);

    // Внутренние изображения
    newSlide.value.internalImageFiles.forEach((file) => {
      formData.append('internal_images', file);
    });

    // Метаданные
    formData.append('title', newSlide.value.title);
    formData.append('description', newSlide.value.description);

    // Extract action_type value if it's an object, otherwise use as string
    const actionTypeValue =
      typeof newSlide.value.action_type === 'object'
        ? newSlide.value.action_type?.value || 'none'
        : newSlide.value.action_type || 'none';
    formData.append('action_type', actionTypeValue);

    formData.append('action_label', newSlide.value.action_label || '');
    formData.append('is_active', String(newSlide.value.is_active));
    formData.append('sort_order', String(newSlide.value.sort_order));

    // Action data (если есть)
    if (newSlide.value.action_data_json) {
      try {
        const actionData = JSON.parse(newSlide.value.action_data_json);
        formData.append('action_data', JSON.stringify(actionData));
      } catch {
        errorMessage.value = 'Неверный формат JSON для action_data';
        uploading.value = false;
        return;
      }
    }

    // Используем useApi для правильного формирования запроса
    const api = useApi(true, true);
    await api.post('/admin/news-slider', {
      body: formData,
    });

    toast.add({
      title: 'Слайд создан!',
      description: 'Новый слайд успешно добавлен',
      color: 'green',
      timeout: 3000,
    });

    // Обновляем список
    await loadSlides();

    // Сбрасываем форму
    newSlide.value = {
      title: '',
      description: '',
      mainImageFile: null,
      internalImageFiles: [],
      action_type: 'none',
      action_label: '',
      action_data_json: '',
      sort_order: slides.value.length + 1,
      is_active: true,
    };

    // Очищаем inputs
    if (mainImageInput.value) mainImageInput.value.value = '';
    if (internalImagesInput.value) internalImagesInput.value.value = '';
  } catch (error: any) {
    consola.error('Ошибка создания слайда:', error);
    errorMessage.value = error.data?.message || error.message || 'Ошибка создания слайда';

    toast.add({
      title: 'Ошибка',
      description: errorMessage.value,
      color: 'red',
      timeout: 5000,
    });
  } finally {
    uploading.value = false;
  }
}

// Загрузка слайдов
async function loadSlides() {
  try {
    loading.value = true;

    // Создаем API клиент
    const api = useApi(true, true);
    const response = await api.get<any>('/admin/news-slider');
    const rawSlides = response.data?.slides || [];

    // Нормализуем данные: преобразуем null в пустой массив для internal_slides
    slides.value = rawSlides.map((slide: any) => ({
      ...slide,
      internal_slides: slide.internal_slides || [],
      image_url: slide.image_url || '',
    }));

    // Очищаем ошибки загрузки изображений при перезагрузке
    imageLoadErrors.value = {};

    consola.log('Слайды загружены:', slides.value);
  } catch (error: any) {
    consola.error('Ошибка загрузки слайдов:', error);
    errorMessage.value = 'Ошибка загрузки слайдов';
  } finally {
    loading.value = false;
  }
}

// Добавить внутренние изображения
async function handleAddInternalImages(slide: NewsSlide, event: Event) {
  if (!slide.id) return;

  const target = event.target as HTMLInputElement;
  if (!target.files || target.files.length === 0) return;

  try {
    const formData = new FormData();
    [...target.files].forEach((file) => {
      formData.append('internal_images', file);
    });

    const api = useApi(true, true);
    await api.post(`/admin/news-slider/${slide.id}/internal-images`, {
      body: formData,
    });

    toast.add({
      title: 'Изображения добавлены',
      description: `Добавлено ${target.files.length} изображений`,
      color: 'green',
      timeout: 2000,
    });

    await loadSlides();
    target.value = '';
  } catch (error: any) {
    consola.error('Ошибка добавления изображений:', error);
    toast.add({
      title: 'Ошибка',
      description: 'Не удалось добавить изображения',
      color: 'red',
      timeout: 3000,
    });
  }
}

// Обновить главное изображение
async function handleUpdateMainImage(slide: NewsSlide, event: Event) {
  if (!slide.id) return;

  const target = event.target as HTMLInputElement;
  if (!target.files || !target.files[0]) return;

  try {
    const formData = new FormData();
    formData.append('image', target.files[0]);

    const api = useApi(true, true);
    await api.put(`/admin/news-slider/${slide.id}/image`, {
      body: formData,
    });

    toast.add({
      title: 'Обложка обновлена',
      description: 'Новое изображение загружено',
      color: 'green',
      timeout: 2000,
    });

    await loadSlides();
    target.value = '';
  } catch (error: any) {
    consola.error('Ошибка обновления изображения:', error);
    toast.add({
      title: 'Ошибка',
      description: 'Не удалось обновить изображение',
      color: 'red',
      timeout: 3000,
    });
  }
}

// Обновить данные слайда
async function updateSlideData(slide: NewsSlide) {
  if (!slide.id) return;

  try {
    const api = useApi(true, true);
    await api.put(`/admin/news-slider/${slide.id}`, {
      body: {
        title: slide.title,
        description: slide.description,
        sort_order: slide.sort_order,
        is_active: slide.is_active,
      },
    });

    toast.add({
      title: 'Слайд обновлен',
      description: 'Изменения сохранены',
      color: 'green',
      timeout: 2000,
    });
  } catch (error: any) {
    consola.error('Ошибка обновления слайда:', error);
    toast.add({
      title: 'Ошибка',
      description: 'Не удалось обновить слайд',
      color: 'red',
      timeout: 3000,
    });
  }
}

// Обновить статус слайда
async function updateSlideStatus(slide: NewsSlide) {
  await updateSlideData(slide);
}

// Удалить слайд
async function confirmDeleteSlide(slide: NewsSlide, index: number) {
  if (!confirm(`Удалить слайд "${slide.title}"?`)) return;

  if (!slide.id) {
    slides.value.splice(index, 1);
    return;
  }

  try {
    const api = useApi(true, true);
    await api.delete(`/admin/news-slider/${slide.id}`);

    toast.add({
      title: 'Слайд удален',
      description: 'Слайд успешно удален',
      color: 'green',
      timeout: 2000,
    });

    await loadSlides();
  } catch (error: any) {
    consola.error('Ошибка удаления слайда:', error);
    toast.add({
      title: 'Ошибка',
      description: 'Не удалось удалить слайд',
      color: 'red',
      timeout: 3000,
    });
  }
}

onMounted(() => {
  emit('update:size', 'xl');
  loadSlides();
});
</script>
